/**
 * Runtime configuration.
 *
 * Production containers write `/config.js`, which sets
 * `window.__MAILSMITH_CONFIG__` before the bundle loads. Any value left empty
 * there falls back to the `VITE_*` value baked in at build or dev time, so the
 * same image works in every environment and `vite dev` works with only env vars.
 */

export type AuthMode = 'dev' | 'entra'

export interface EntraConfig {
  clientId: string
  tenantId: string
  apiScope: string
}

export interface AppConfig {
  apiBaseUrl: string
  authMode: AuthMode
  entra: EntraConfig
}

interface RawConfig {
  apiBaseUrl?: string
  authMode?: string
  entra?: Partial<EntraConfig>
}

declare global {
  interface Window {
    __MAILSMITH_CONFIG__?: RawConfig
  }
}

function pick(...values: (string | undefined)[]): string {
  for (const v of values) {
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
  }
  return ''
}

export function resolveConfig(
  raw: RawConfig | undefined,
  env: Record<string, string | undefined>,
): AppConfig {
  const mode = pick(raw?.authMode, env.VITE_AUTH_MODE, 'dev')
  if (mode !== 'dev' && mode !== 'entra') {
    throw new Error(`Unsupported auth mode "${mode}". Use "dev" or "entra".`)
  }
  const config: AppConfig = {
    apiBaseUrl: pick(raw?.apiBaseUrl, env.VITE_API_BASE_URL, 'http://localhost:5000').replace(
      /\/+$/,
      '',
    ),
    authMode: mode,
    entra: {
      clientId: pick(raw?.entra?.clientId, env.VITE_ENTRA_CLIENT_ID),
      tenantId: pick(raw?.entra?.tenantId, env.VITE_ENTRA_TENANT_ID),
      apiScope: pick(raw?.entra?.apiScope, env.VITE_ENTRA_API_SCOPE),
    },
  }
  if (config.authMode === 'entra') {
    const missing = (['clientId', 'tenantId', 'apiScope'] as const).filter((k) => !config.entra[k])
    if (missing.length > 0) {
      throw new Error(
        `Entra sign-in is enabled but these settings are empty: ${missing.join(', ')}.`,
      )
    }
  }
  return config
}

let cached: AppConfig | undefined

/** The resolved configuration. Throws a descriptive error if it is invalid. */
export function getConfig(): AppConfig {
  cached ??= resolveConfig(
    typeof window === 'undefined' ? undefined : window.__MAILSMITH_CONFIG__,
    import.meta.env as Record<string, string | undefined>,
  )
  return cached
}
